import { Info, Save } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from 'react-toastify';
import CustomSelect from "../components/CustomSelect";
import { listPrompts, getPrompt, createPrompt, updatePrompt } from "../services/promptService";
import { getProjectPid } from "../utils/sessionProject";

const PROMPT_TYPE_LABELS = {
  "welcome_message": "Welcome Message",
  "error-message": "Error Message",
  "system_prompt_chat": "System Prompt – Chat",
  "system_prompt_voice": "System Prompt – Voice",
  "system_prompt_website_to_document": "Website → Document",
  "system_prompt_pdf_to_document": "PDF → Document",
  "document_to_chunks": "Document → Chunks",
  "system_prompt_document_to_chunks": "Document → Chunks",
  "fallback_response": "Fallback Response",
  "safety_filter": "Safety Filter",
  "faq_response": "FAQ Response",
};

const Prompts = () => {
  const editorRef = useRef(null);
  const projectPid = getProjectPid();

  const [promptTypes, setPromptTypes] = useState([]);
  const [promptsList, setPromptsList] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [promptPid, setPromptPid] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [temperature, setTemperature] = useState(0.7);

  // On mount: load list of prompts, build dropdown from API response
  useEffect(() => {
    const load = async () => {
      try {
        const res = await listPrompts({ projectPid });
        const items = Array.isArray(res?.response) ? res.response : [];
        setPromptsList(items);
        const types = items.map((p) => ({
          value: p.prompt_type,
          label: PROMPT_TYPE_LABELS[p.prompt_type] || p.prompt_type,
        }));
        setPromptTypes(types);
        if (types.length) setActiveType(types[0].value);
      } catch {
        setPromptTypes([]);
      }
    };
    load();
  }, [projectPid]);

  // When activeType or promptsList changes: find prompt_pid, call getPrompt
  useEffect(() => {
    if (!activeType) return;

    const matched = promptsList.find((p) => p.prompt_type === activeType);
    const pid = matched?.prompt_pid ?? null;
    setPromptPid(pid);

    if (!pid) {
      if (editorRef.current) { editorRef.current.innerText = ""; setCharCount(0); }
      setTemperature(0.7);
      return;
    }

    const fetchContent = async () => {
      try {
        const res = await getPrompt({ projectPid, promptPid: pid });
        const data = res?.response;
        // try all possible content field names
        const content = data?.content ?? data?.prompt ?? data?.text ?? data?.body ?? "";
        if (editorRef.current) {
          editorRef.current.innerText = content;
          setCharCount(content.length);
        }
        setTemperature(typeof data?.temperature === "number" ? data.temperature : 0.7);
      } catch {
        if (editorRef.current) { editorRef.current.innerText = ""; setCharCount(0); }
        setTemperature(0.7);
      }
    };
    fetchContent();
  }, [activeType, promptsList, projectPid]);

  const handleTypeChange = (value) => setActiveType(value);

  const handleInput = () => {
    if (editorRef.current) setCharCount(editorRef.current.textContent.length);
  };

  const handleSave = async () => {
    const content = editorRef.current?.innerText || "";
    setSaving(true);
    try {
      const matched = promptsList.find((p) => p.prompt_type === activeType);
      const prompt_pid = matched?.prompt_pid ?? null;
      if (prompt_pid) {
        await updatePrompt({ prompt_pid, content, temperature });
      } else {
        const res = await createPrompt({ projectPid, promptType: activeType, content, temperature });
        const newPid = res?.response?.prompt_pid ?? res?.response?.promptPid;
        if (newPid) {
          setPromptPid(newPid);
          setPromptsList((prev) => [...prev, { prompt_type: activeType, prompt_pid: newPid }]);
        }
      }
      toast.success("Prompt saved successfully!");
    } catch (err) {
      console.error('Save prompt failed', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTemperatureChange = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return;
    setTemperature(Math.min(2, Math.max(0, num)));
  };

  const currentLabel = promptTypes.find((t) => t.value === activeType)?.label || activeType;

  return (
    <main className="app-wrapper url-list-page" id="promptPage">
      <div className="container-fluid">

        <div className="ps-topbar mb-3">
          <div className="ps-type-select-wrap">
            <p className="kbd-selector-label">Prompt Type</p>
            <CustomSelect
              value={activeType}
              onChange={handleTypeChange}
              options={promptTypes}
              className="ps-csel"
            />
          </div>
          <button
            className="btn btn-primary ps-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={15} className="me-2" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        <div className="ps-editor-card">
          <div className="ps-editor-hd">
            <div className="ps-editor-hd-left">
              <div className="ps-editor-type-icon" style={{ background: "var(--primaryTint)", color: "var(--primaryColor)" }}>
                <span className="ps-t-letter">T</span>
              </div>
              <div>
                <div className="ps-editor-title-row">
                  <h3 className="ps-editor-title">{currentLabel}</h3>
                  <span className="ps-active-badge">Active</span>
                </div>
              </div>
            </div>
            <span className="ps-char-badge">{charCount} chars</span>
          </div>

          <div className="ps-temp-row">
            <label className="ps-temp-label" htmlFor="ps-temp-range">
              Temperature
              <span className="ps-temp-hint">Controls randomness — lower is more focused, higher is more creative</span>
            </label>
            <div className="ps-temp-controls">
              <input
                id="ps-temp-range"
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={temperature}
                onChange={(e) => handleTemperatureChange(e.target.value)}
                className="ps-temp-slider"
              />
              <input
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={temperature}
                onChange={(e) => handleTemperatureChange(e.target.value)}
                className="ps-temp-number"
              />
            </div>
          </div>

          <div
            ref={editorRef}
            className="ps-editor-area"
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            data-placeholder={`Enter the ${currentLabel?.toLowerCase()} prompt here...`}
          />

          <div className="ps-char-row">
            <span>{charCount} characters</span>
          </div>

          <div className="ps-footer-info">
            <Info size={14} className="flex-shrink-0" />
            <span>
              Changes are saved per prompt type. Don&apos;t forget to click{" "}
              <strong>Save Changes</strong>.
            </span>
          </div>
        </div>

      </div>
    </main>
  );
};

export default Prompts;
